import { Fragment, Component, lazy, Suspense } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { layoutActions } from 'store/layout';
import { accountMgmtActions } from 'store/accountMgmt';
import BlockUi from 'components/common/BlockUi';
import { ScaleLoader } from 'react-spinners';

const ReportingRoot = lazy(() => import("containers/reporting/RootComponent"));
const ProjectManagement = lazy(() => import("containers/ProjectManagement"));
const TaskManagement = lazy(() => import("containers/TaskManagement"));
const ScenarioStatus = lazy(() => import("containers/ScenarioStatus"));
const TaskRoot = lazy(() => import('containers/task/RootComponent'));
const AdminManagement = lazy(() => import('containers/adminManagement/RootComponent'));

import PrivateRoute from "components/common/PrivateRoute";
import { ErrorModal, Header, EventsListModal } from 'components/layout'
import { ChangePasswordModal } from "components/accountManager";
import PageNotFound from "components/errors/PageNotFound";
import Forbidden from "components/errors/Forbidden";
import { parseNotifications } from "common/helpers";
import { NavLink as RRNavLink, Switch } from "react-router-dom";
import { NavItem, NavLink, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dashboardMgmtActions } from 'store/dashboardMgmt';
import { AccountMgmtTypes } from 'store/accountMgmt';

import cx from 'classnames';
import { RootState } from 'store';

type OwnProps = {
    signOut: () => void;
};

const mapStateToProps = (state: RootState) => ({
    passwordModal: state.accountMgmtSlice.passwordResetModal,
    ...state.layoutSlice,
});

const mapDispatchToProps = {
    ...layoutActions,
    ...accountMgmtActions,
    getSnowflakeConfiguration: dashboardMgmtActions.getConfiguration,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = ConnectedProps<typeof connector> & OwnProps;

class LayoutContainer extends Component<Props> {
    state = {
        isEventsListOpen: false,
    };

    async componentDidMount() {
        const {
            fetchUser,
            setCurrentUser,
            getFeaturesConfiguration,
            setFeaturesConfiguration,
        } = this.props;


        const [configuration, user] = await Promise.all([
            getFeaturesConfiguration().unwrap(),
            fetchUser().unwrap(),
        ]);

        setFeaturesConfiguration(configuration);
        setCurrentUser(user);
    }

    constructor(props: Props) {
        super(props);
    }

    changePassword = () => {
        const { currentUser, openPasswordResetModal } = this.props;

        openPasswordResetModal({
            header: 'Change password',
            source: {
                userId: currentUser?.userId!,
                currentUser: currentUser!,
                password: null,
                confirmPassword: null
            }
        });
    };

    onChangePasswordSubmit = async (formData: AccountMgmtTypes.PasswordResetFormData) => {
        const { changePassword, togglePasswordModalSpinner, passwordModal } = this.props;

        togglePasswordModalSpinner(true);
        try {
            await changePassword({
                password: formData.password,
                currentPassword: formData.currentPassword,
                userId: passwordModal.source.userId || ""
            }).unwrap();
        } finally {
            togglePasswordModalSpinner(false);
        }
    };

    onCleanStatuses = () => this.props.cleanStatuses();

    onDismissStatus = (procGuid: string) => this.props.dismissStatus(procGuid);

    openEventsList = () => this.setState({ isEventsListOpen: true });

    closeEventsList = () => this.setState({ isEventsListOpen: false });

    onSearch = async (value: string) => {
        const { fetchScenario, toggleGeneralSpinner } = this.props;

        if (!value.match(/^\d+$/)) {
            parseNotifications({ errors: ['Invalid case ID specified.'], warnings: [], infos: [] })
        }

        toggleGeneralSpinner(true);
        try {
            const scenario = await fetchScenario(value).unwrap();
            if (!scenario) {
                parseNotifications({ errors: [`Case "${value}" not found.`], warnings: [], infos: [] });
                return;
            }

            window.location.href = `/status/${value}`
        } finally {
            toggleGeneralSpinner(false);
        }
    }

    onOpenSnowflake = async () => {
        const config = await this.props.getSnowflakeConfiguration().unwrap();
        window.open(config.appUrl, '_blank');
    };

    render() {

        const {
            generalSpinner,
            dashboardStatuses,
            jobStatuses,
            currentUser,
            navigation,
            featuresConfiguration,
            signOut,
            sidebarMenu,
            passwordModal,
            closePasswordResetModal
        } = this.props;
        const { isEventsListOpen } = this.state;

        if (!currentUser) {
            return <BlockUi blocking renderChildren={false} className="vh-100 d-flex align-items-center justify-content-center" />;
        }

        const events = dashboardStatuses.concat(jobStatuses);
        const sidebarVisible = sidebarMenu.length > 0;

        return (
            <Fragment>
                <BlockUi blocking={generalSpinner}>
                    <div className={cx("app-wrapper  vh-100 bg-gray-100", { 'app-sidebar-fixed app-header-fixed app-sidebar-collapsed': sidebarVisible })} >
                        {sidebarVisible &&
                            <div className="app-sidebar app-sidebar--dark" style={{ zIndex: 999 }}>
                                <div className="app-sidebar--header">

                                </div>
                                <div className="app-sidebar--content">
                                    <div className="sidebar-menu-collapsed py-2">
                                        {sidebarMenu.map(n => (
                                            <NavItem key={n.idx} id={`nav-menu-item-${n.idx}`}>
                                                <NavLink {...(n.onClick
                                                    ? { onClick: n.onClick, className: 'cursor-pointer' }
                                                    : { tag: RRNavLink, to: n.link })}
                                                >
                                                    <div className="nav-link-icon">
                                                        {'prefix' in n.icon ? <FontAwesomeIcon icon={n.icon} size="lg" /> : n.icon}
                                                    </div>
                                                </NavLink>
                                                <UncontrolledTooltip popperClassName="tooltip-secondary" placement="right" container="body" boundariesElement="window" target={`nav-menu-item-${n.idx}`}>{n.label}</UncontrolledTooltip>
                                            </NavItem>))
                                        }
                                    </div>
                                </div>
                            </div>}
                        <div className="app-main">
                            <Header
                                actions={{
                                    signOut: signOut,
                                    changePassword: this.changePassword,
                                    search: this.onSearch,
                                    openSnowflake: this.onOpenSnowflake,
                                    openEventsList: this.openEventsList,
                                }}
                                data={{ currentUser, eventsCount: events.length, featuresConfiguration }}
                                navigation={navigation}
                            />
                            <div className="app-content">
                                <div className="app-inner-content-layout--main">
                                    <div className="app-inner-content-layout h-100">
                                        <div className="app-inner-content-layout--main order-3 order-lg-2 card-box bg-secondary p-3 mh-100 mw-100">
                                            <Suspense fallback={
                                                <div className="d-flex justify-content-center py-5">
                                                    <ScaleLoader color="var(--first)" loading />
                                                </div>
                                            }>
                                                <Switch>
                                                    <PrivateRoute exact path="/" component={ProjectManagement} />
                                                    <PrivateRoute path="/reporting/:taskId" component={ReportingRoot} />
                                                    <PrivateRoute path="/projects" component={ProjectManagement} />
                                                    <PrivateRoute path="/project/:projectId" component={TaskManagement} />
                                                    <PrivateRoute path="/task/:taskId" component={TaskRoot} />
                                                    <PrivateRoute path="/status/:scenarioId" component={ScenarioStatus} />
                                                    <PrivateRoute path="/notAuthorized" component={PageNotFound} />
                                                    <PrivateRoute path='/forbidden' component={Forbidden} />
                                                    <PrivateRoute path='/management' component={AdminManagement} />
                                                    <PrivateRoute component={PageNotFound} />
                                                </Switch>
                                            </Suspense>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <EventsListModal
                        events={events}
                        isOpen={isEventsListOpen}
                        onClose={this.closeEventsList}
                        actions={{
                            clean: this.onCleanStatuses,
                            dismiss: this.onDismissStatus
                        }}
                    />

                    <ConfirmationDialog />

                    <ChangePasswordModal
                        header={passwordModal.header}
                        open={passwordModal.open}
                        locked={passwordModal.locked}
                        source={passwordModal.source}
                        actions={{
                            close: closePasswordResetModal,
                            submit: this.onChangePasswordSubmit,
                        }}
                    />
                    <ErrorModal />
                </BlockUi>
            </Fragment>
        );
    }
};

export default connector(LayoutContainer);
